import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CashCategory, CashFlowType, PaymentMethod } from '@core/types';
import Layout from '../componentes/Layout';
import Aviso from '../componentes/Aviso';
import Carregando from '../componentes/Carregando';
import {
  abrirCaixa, caixaDiario, caixaDoDia, conferirVenda, desconferirVenda, fecharCaixa,
  lancarCaixa, listarCaixa, vendasDoPeriodo, type VendaResumo,
} from '../lib/consultas';
import { baixarCsv, paraCsv } from '../lib/csv';
import { brl, dataBR, dataHoraBR, hojeISO, horaBR, intervaloDoDia, mesISO, paraNumero } from '../lib/formato';
import { descreverPagamento, parteEmDinheiro } from '../lib/pagamentos';
import { mensagemDeErro } from '../lib/supabase';

// Tela 7 — caixa. A dor original: "zero visibilidade de entrada e saída por
// dia/hora/mês". Por isso o recorte de tempo é o controle principal da tela,
// e não um filtro escondido: dia com faixa de horário, ou mês inteiro.
//
// Vendas entram aqui sozinhas (a RPC registrar_venda lança). O que se digita
// nesta tela é o que não passa pela venda: despesa, sangria, aporte, frete.

const CATEGORIAS: Array<{ id: CashCategory; rotulo: string; tipo: CashFlowType | 'ambos' }> = [
  { id: 'venda', rotulo: 'Venda', tipo: 'entrada' },
  { id: 'aporte', rotulo: 'Aporte', tipo: 'entrada' },
  { id: 'compra_mercadoria', rotulo: 'Compra de mercadoria', tipo: 'saida' },
  { id: 'frete', rotulo: 'Frete', tipo: 'saida' },
  { id: 'despesa_fixa', rotulo: 'Despesa fixa', tipo: 'saida' },
  { id: 'despesa_variavel', rotulo: 'Despesa variável', tipo: 'saida' },
  { id: 'salario', rotulo: 'Salário', tipo: 'saida' },
  { id: 'imposto', rotulo: 'Imposto', tipo: 'saida' },
  { id: 'sangria', rotulo: 'Sangria', tipo: 'saida' },
  { id: 'outro', rotulo: 'Outro', tipo: 'ambos' },
];

/**
 * As categorias que o "+ Lançar" oferece — o FILTRO continua com todas.
 *
 * Compra de mercadoria saiu do lançamento à mão em 08/10/2026 (095): ela tem
 * caminho próprio, "Paguei fornecedor", que grava a compra como conta paga.
 * Lançada aqui, ela entraria no livro sem conta — fora da lista do mês e de
 * Contas a pagar —, e o dono teria dois lugares que não batem. Um caminho só.
 */
export function categoriasDoLancamento(tipo: CashFlowType) {
  return CATEGORIAS.filter((c) => (c.tipo === tipo || c.tipo === 'ambos') && c.id !== 'compra_mercadoria');
}

// Cor por categoria (17/08/2026, pedido do dono): identidade visual estável
// para bater o olho na lista e achar o assunto sem ler linha a linha. São
// cores da paleta padrão do Tailwind (o @theme SOMA tokens, não substitui a
// paleta) — e nenhuma delas é o vermelho da marca nem o verde/vermelho de
// resultado, que continuam significando só entrada/saída (regra 10).
const COR_CATEGORIA: Record<CashCategory, string> = {
  venda: 'bg-emerald-400',
  aporte: 'bg-sky-400',
  compra_mercadoria: 'bg-orange-400',
  frete: 'bg-amber-300',
  despesa_fixa: 'bg-violet-400',
  despesa_variavel: 'bg-fuchsia-400',
  salario: 'bg-cyan-400',
  imposto: 'bg-rose-400',
  sangria: 'bg-red-500',
  outro: 'bg-stone-400',
};

const FORMAS: PaymentMethod[] = [
  'dinheiro', 'pix', 'debito', 'credito', 'boleto', 'transferencia', 'fiado',
];

const HORAS = Array.from({ length: 25 }, (_, h) => h);

export default function Caixa() {
  const cliente = useQueryClient();
  const [modo, setModo] = useState<'dia' | 'mes'>('dia');
  const [dia, setDia] = useState(hojeISO());
  const [mes, setMes] = useState(mesISO());
  const [horaDe, setHoraDe] = useState(0);
  const [horaAte, setHoraAte] = useState(24);
  const [lancando, setLancando] = useState(false);
  // A gaveta manda gastar: "pagar conta", "comprar peça", "retirar salário"
  // abrem o MESMO formulário de lançamento já apontado para dinheiro/saída —
  // o dono pediu a ação, não uma tabela nova (é saída de caixa como qualquer
  // outra, e continua caindo no livro).
  const [presetLanc, setPresetLanc] = useState<PresetLancamento | null>(null);
  const [filtroTipo, setFiltroTipo] = useState<CashFlowType | 'todos'>('todos');
  const [filtroCategoria, setFiltroCategoria] = useState<CashCategory | 'todas'>('todas');
  const [filtroForma, setFiltroForma] = useState<PaymentMethod | 'todas'>('todas');

  const periodo = useMemo(() => {
    if (modo === 'dia') return intervaloDoDia(dia, horaDe, horaAte);
    const inicio = new Date(`${mes}-01T00:00:00`);
    const fim = new Date(inicio);
    fim.setMonth(fim.getMonth() + 1);
    return { inicio: inicio.toISOString(), fim: fim.toISOString() };
  }, [modo, dia, mes, horaDe, horaAte]);

  const { data: movimentos, isLoading, error } = useQuery({
    queryKey: ['caixa', 'movimentos', periodo.inicio, periodo.fim,
               filtroTipo, filtroCategoria, filtroForma],
    queryFn: () => listarCaixa({
      de: periodo.inicio, ate: periodo.fim,
      tipo: filtroTipo, categoria: filtroCategoria, forma: filtroForma,
    }),
  });

  const { data: porDia } = useQuery({
    queryKey: ['caixa', 'diario', mes],
    queryFn: () => {
      const fim = new Date(`${mes}-01T00:00:00`);
      fim.setMonth(fim.getMonth() + 1);
      fim.setDate(0);
      return caixaDiario(`${mes}-01`, hojeISO(fim));
    },
    enabled: modo === 'mes',
  });

  // O BALCÃO AGORA (17/08/2026). Pedido do dono: "visualizar o que foi vendido
  // e o que veio para o caixa para eu realizar o troco ou a cobrança em
  // pix/cartão". É SEMPRE hoje, independente do recorte abaixo, e se atualiza
  // sozinho — a tela fica aberta do lado do caixa físico. O intervalo é
  // recalculado a cada busca (queryFn), então virar a meia-noite não congela
  // a tela no dia anterior.
  const { data: vendasHoje } = useQuery({
    queryKey: ['caixa', 'balcao', 'vendas'],
    queryFn: () => {
      const h = intervaloDoDia(hojeISO());
      return vendasDoPeriodo(h.inicio, h.fim);
    },
    refetchInterval: 15_000,
  });
  const { data: caixaHoje } = useQuery({
    queryKey: ['caixa', 'balcao', 'movimentos'],
    queryFn: () => {
      const h = intervaloDoDia(hojeISO());
      return listarCaixa({ de: h.inicio, ate: h.fim });
    },
    refetchInterval: 15_000,
  });
  const balcao = useMemo(() => {
    // Cancelada sai da conta do vendido (o estorno dela já aparece no caixa);
    // fiado FICA no vendido mas não no "entrou" — é exatamente a diferença que
    // o dono precisa enxergar na hora de cobrar (042).
    const validas = (vendasHoje ?? []).filter((v) => !v.cancelada_em);
    const porForma = new Map<string, number>();
    let entrou = 0;
    for (const m of caixaHoje ?? []) {
      if (m.tipo !== 'entrada') continue;
      entrou += Number(m.valor);
      const k = m.forma_pagamento ?? 'sem forma';
      porForma.set(k, (porForma.get(k) ?? 0) + Number(m.valor));
    }
    return {
      vendas: validas.slice(0, 12),
      quantasVendas: validas.length,
      totalVendido: validas.reduce((s, v) => s + Number(v.total), 0),
      entrou,
      porForma: [...porForma.entries()].sort((a, b) => b[1] - a[1]),
    };
  }, [vendasHoje, caixaHoje]);

  const totais = useMemo(() => {
    const linhas = movimentos ?? [];
    const entradas = linhas.filter((m) => m.tipo === 'entrada').reduce((s, m) => s + Number(m.valor), 0);
    const saidas = linhas.filter((m) => m.tipo === 'saida').reduce((s, m) => s + Number(m.valor), 0);

    const agrupar = <T extends string>(chave: (m: (typeof linhas)[number]) => T | null) => {
      const mapa = new Map<T, { entrada: number; saida: number }>();
      for (const m of linhas) {
        const k = chave(m);
        if (k === null) continue;
        const atual = mapa.get(k) ?? { entrada: 0, saida: 0 };
        atual[m.tipo] += Number(m.valor);
        mapa.set(k, atual);
      }
      return [...mapa.entries()].sort((a, b) =>
        (b[1].entrada + b[1].saida) - (a[1].entrada + a[1].saida));
    };

    return {
      entradas,
      saidas,
      saldo: entradas - saidas,
      quantidade: linhas.length,
      porCategoria: agrupar((m) => m.categoria),
      porForma: agrupar((m) => m.forma_pagamento),
      porHora: (() => {
        const mapa = new Map<number, { entrada: number; saida: number }>();
        for (const m of linhas) {
          const h = new Date(m.ocorrido_em).getHours();
          const atual = mapa.get(h) ?? { entrada: 0, saida: 0 };
          atual[m.tipo] += Number(m.valor);
          mapa.set(h, atual);
        }
        return [...mapa.entries()].sort((a, b) => a[0] - b[0]);
      })(),
    };
  }, [movimentos]);

  const exportar = () => {
    const linhas = (movimentos ?? []).map((m) => [
      dataHoraBR(m.ocorrido_em), m.tipo, m.categoria, m.forma_pagamento ?? '',
      Number(m.valor).toFixed(2).replace('.', ','), m.descricao ?? '',
    ]);
    const nome = modo === 'dia' ? `caixa-${dia}.csv` : `caixa-${mes}.csv`;
    baixarCsv(nome, paraCsv(
      ['Data/hora', 'Tipo', 'Categoria', 'Forma', 'Valor', 'Descrição'],
      linhas,
    ));
  };

  // Escala das barras: o maior valor da hora, entrada OU saída — senão uma
  // compra grande estoura a largura da linha.
  const maiorHora = Math.max(1, ...totais.porHora.flatMap(([, v]) => [v.entrada, v.saida]));

  return (
    <Layout
      titulo="Caixa"
      acao={
        <div className="flex items-center gap-2">
          {/* A compra paga na hora (095) tem caminho próprio: vira conta
              paga, e o mês dela fica inteiro numa lista só.
              Só a partir do `sm`: no celular o cabeçalho não tem lugar para
              dois botões. Com este link ao lado do "+ Lançar", a 375 px o
              grupo da direita passava da tela (383 px com "Sergio", 406 com
              um nome maior), a página rolava de lado e o título "Caixa" ia a
              zero — medido no WebKit e no Chromium. No celular, o mesmo
              caminho é o cartão logo abaixo da gaveta. */}
          <Link to="/compra-na-hora"
                className="botao-secundario hidden px-3 py-2 text-xs sm:inline-block">
            Paguei fornecedor
          </Link>
          <button
            type="button"
            onClick={() => setLancando((v) => !v)}
            className="botao-primario px-3 py-2 text-xs"
          >
            + Lançar
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {lancando && (
          <NovoLancamento
            key={presetLanc ? `${presetLanc.categoria}-${presetLanc.tipo}` : 'livre'}
            inicial={presetLanc}
            aoFechar={() => { setLancando(false); setPresetLanc(null); }}
            aoSalvar={() => {
              setLancando(false);
              setPresetLanc(null);
              cliente.invalidateQueries({ queryKey: ['caixa'] });
            }}
          />
        )}

        {/* A GAVETA (055). Primeira coisa da tela porque é a primeira pergunta
            do dia do dono: "quanto tem de dinheiro no caixa?" */}
        <GavetaDoDia
          aoGastar={(preset) => { setPresetLanc(preset); setLancando(true); }}
        />

        {/* "Paguei fornecedor" no celular. O lugar dele no cabeçalho só existe
            a partir do `sm` (ver o `acao` acima); aqui, logo abaixo da gaveta,
            que continua a primeira coisa da tela. */}
        <Link to="/compra-na-hora"
              className="cartao flex items-center justify-between gap-3 px-4 py-3 text-sm hover:border-signal sm:hidden">
          <span>Pagou fornecedor agora?</span>
          <span className="shrink-0 font-bold text-signal-soft">Paguei fornecedor →</span>
        </Link>

        {/* O balcão agora — ver o comentário nas queries. Fica ACIMA do
            recorte: é a razão de o dono abrir esta tela durante o expediente. */}
        <section className="cartao space-y-3 p-4">
          <div className="flex items-baseline justify-between">
            <h2 className="rotulo">Balcão agora</h2>
            <span className="text-[0.625rem] uppercase tracking-wide text-dim">
              hoje · atualiza a cada 15s
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <div>
              <p className="rotulo">Vendido hoje ({balcao.quantasVendas})</p>
              <p className="text-base font-black whitespace-nowrap tabular-nums sm:text-2xl">
                {brl(balcao.totalVendido)}
              </p>
            </div>
            <div>
              <p className="rotulo">Entrou no caixa</p>
              <p className="text-base font-black whitespace-nowrap tabular-nums text-ok sm:text-2xl">
                {brl(balcao.entrou)}
              </p>
            </div>
          </div>

          {/* A conferência da cobrança: dinheiro é o que tem que bater com a
              gaveta; pix/cartão, com o extrato da maquininha. */}
          {balcao.porForma.length > 0 && (
            <ul className="flex flex-wrap gap-1.5 text-xs">
              {balcao.porForma.map(([forma, valor]) => (
                <li key={forma} className="border border-edge px-2 py-1 font-mono tabular-nums">
                  <span className="capitalize text-dim">{forma}</span>{' '}
                  <span className="font-bold">{brl(valor)}</span>
                </li>
              ))}
            </ul>
          )}

          {balcao.vendas.length > 0 ? (
            <ul className="divide-y divide-edge">
              {balcao.vendas.map((v) => <LinhaDoBalcao key={v.id} v={v} />)}
            </ul>
          ) : (
            <p className="text-xs text-mute">Nenhuma venda hoje ainda.</p>
          )}
        </section>

        {/* Recorte de tempo */}
        <section className="cartao space-y-3 p-4">
          <div className="flex gap-2">
            {(['dia', 'mes'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setModo(m)}
                aria-pressed={modo === m}
                className={[
                  'flex-1 border px-4 py-2.5 text-sm font-bold',
                  modo === m ? 'border-mute bg-edge text-paper' : 'border-edge text-mute',
                ].join(' ')}
              >
                {m === 'dia' ? 'Por dia' : 'Por mês'}
              </button>
            ))}
          </div>

          {modo === 'dia' ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="rotulo" htmlFor="dia">Dia</label>
                <input id="dia" type="date" className="campo" value={dia}
                       onChange={(e) => setDia(e.target.value)} />
              </div>
              <div>
                <label className="rotulo" htmlFor="hora-de">Das</label>
                <select id="hora-de" className="campo" value={horaDe}
                        onChange={(e) => setHoraDe(Number(e.target.value))}>
                  {HORAS.slice(0, 24).map((h) => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="rotulo" htmlFor="hora-ate">Até</label>
                <select id="hora-ate" className="campo" value={horaAte}
                        onChange={(e) => setHoraAte(Number(e.target.value))}>
                  {HORAS.slice(1).map((h) => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div>
              <label className="rotulo" htmlFor="mes">Mês</label>
              <input id="mes" type="month" className="campo" value={mes}
                     onChange={(e) => setMes(e.target.value)} />
            </div>
          )}

          {/* Filtros (17/08/2026): a data já era o controle principal; agora o
              recorte fecha também por tipo, categoria e forma. Totais, quebras
              e o CSV exportado obedecem — tudo deriva de `movimentos`. */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div>
              <label className="rotulo" htmlFor="f-tipo">Tipo</label>
              <select id="f-tipo" className="campo" value={filtroTipo}
                      onChange={(e) => setFiltroTipo(e.target.value as CashFlowType | 'todos')}>
                <option value="todos">Todos</option>
                <option value="entrada">Entrada</option>
                <option value="saida">Saída</option>
              </select>
            </div>
            <div>
              <label className="rotulo" htmlFor="f-categoria">Categoria</label>
              <select id="f-categoria" className="campo" value={filtroCategoria}
                      onChange={(e) => setFiltroCategoria(e.target.value as CashCategory | 'todas')}>
                <option value="todas">Todas</option>
                {CATEGORIAS.map((c) => (
                  <option key={c.id} value={c.id}>{c.rotulo}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="rotulo" htmlFor="f-forma">Forma</label>
              <select id="f-forma" className="campo" value={filtroForma}
                      onChange={(e) => setFiltroForma(e.target.value as PaymentMethod | 'todas')}>
                <option value="todas">Todas</option>
                {FORMAS.map((f) => (
                  <option key={f} value={f} className="capitalize">{f}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {error ? <Aviso tipo="erro">{mensagemDeErro(error)}</Aviso> : null}
        {isLoading && <Carregando texto="Somando o caixa" />}

        {/* Totais */}
        {/* Fonte menor no celular de propósito: "R$ 1.075,50" em 3 colunas de
            375px não cabe em text-xl, e valor cortado num painel de caixa é
            pior que valor pequeno. */}
        <section className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="cartao p-3 sm:p-4">
            <p className="rotulo">Entradas</p>
            <p className="text-base font-black whitespace-nowrap tabular-nums text-ok sm:text-2xl">{brl(totais.entradas)}</p>
          </div>
          <div className="cartao p-3 sm:p-4">
            <p className="rotulo">Saídas</p>
            <p className="text-base font-black whitespace-nowrap tabular-nums text-erro sm:text-2xl">{brl(totais.saidas)}</p>
          </div>
          <div className="cartao p-3 sm:p-4">
            <p className="rotulo">Saldo</p>
            <p className={[
              'text-base font-black whitespace-nowrap tabular-nums sm:text-2xl',
              totais.saldo >= 0 ? 'text-paper' : 'text-erro',
            ].join(' ')}>{brl(totais.saldo)}</p>
          </div>
        </section>

        {/* Por hora — responde "que horas a loja vende?" */}
        {modo === 'dia' && totais.porHora.length > 0 && (
          <section className="cartao p-4">
            <h2 className="rotulo">Movimento por hora</h2>
            <ul className="mt-2 space-y-1.5">
              {totais.porHora.map(([hora, v]) => (
                <li key={hora} className="flex items-center gap-2 text-xs">
                  <span className="w-9 shrink-0 font-mono text-dim">{String(hora).padStart(2, '0')}h</span>
                  <span className="flex min-w-0 flex-1 items-center gap-1">
                    {v.entrada > 0 && (
                      <span className="h-3.5 bg-ok" style={{ width: `${(v.entrada / maiorHora) * 55}%` }} />
                    )}
                    {v.saida > 0 && (
                      <span className="h-3.5 bg-erro" style={{ width: `${(v.saida / maiorHora) * 55}%` }} />
                    )}
                  </span>
                  <span className="shrink-0 text-right font-mono tabular-nums">
                    {v.entrada > 0 && <span className="text-ok">{brl(v.entrada)}</span>}
                    {v.entrada > 0 && v.saida > 0 && ' '}
                    {v.saida > 0 && <span className="text-erro">−{brl(v.saida)}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Por dia (modo mês) */}
        {modo === 'mes' && porDia && porDia.length > 0 && (
          <section className="cartao overflow-x-auto p-4">
            <h2 className="rotulo">Por dia</h2>
            <table className="mt-2 w-full text-left text-sm">
              <thead className="text-xs text-dim">
                <tr><th className="py-2">Dia</th><th className="py-2 text-right">Entradas</th>
                    <th className="py-2 text-right">Saídas</th><th className="py-2 text-right">Saldo</th></tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {porDia.map((d) => (
                  <tr key={String(d.dia)}>
                    <td className="py-2">{dataBR(String(d.dia))}</td>
                    <td className="py-2 text-right font-mono text-ok">{brl(Number(d.entradas))}</td>
                    <td className="py-2 text-right font-mono text-erro">{brl(Number(d.saidas))}</td>
                    <td className="py-2 text-right font-mono">{brl(Number(d.saldo))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Quebras */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Quebra titulo="Por categoria" itens={totais.porCategoria}
                  corDa={(c) => COR_CATEGORIA[c as CashCategory]} />
          <Quebra titulo="Por forma de pagamento" itens={totais.porForma} />
        </div>

        {/* Lançamentos */}
        <section className="cartao p-4">
          <div className="flex items-center justify-between">
            <h2 className="rotulo">Lançamentos ({totais.quantidade})</h2>
            <button
              type="button"
              onClick={exportar}
              className="botao-terciario"
              disabled={!movimentos?.length}
            >
              Exportar CSV
            </button>
          </div>

          {movimentos && movimentos.length > 0 ? (
            <ul className="mt-2 divide-y divide-edge">
              {movimentos.map((m) => (
                <li key={m.id} className="flex items-start justify-between gap-3 py-2.5 text-sm">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 font-semibold capitalize">
                      <span className={`size-2 shrink-0 ${COR_CATEGORIA[m.categoria]}`} aria-hidden />
                      {m.categoria.replace(/_/g, ' ')}
                      {m.forma_pagamento && (
                        <span className="font-normal text-dim">{m.forma_pagamento}</span>
                      )}
                    </p>
                    {m.descricao && <p className="truncate text-xs text-mute">{m.descricao}</p>}
                    <p className="font-mono text-xs text-dim">{dataHoraBR(m.ocorrido_em)}</p>
                  </div>
                  <span className={[
                    'shrink-0 font-mono font-bold',
                    m.tipo === 'entrada' ? 'text-ok' : 'text-erro',
                  ].join(' ')}>
                    {m.tipo === 'entrada' ? '+' : '−'}{brl(Number(m.valor))}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            !isLoading && <p className="mt-3 text-sm text-dim">Nenhum lançamento no período.</p>
          )}
        </section>
      </div>
    </Layout>
  );
}

// =============================================================================
// A GAVETA DO DIA (055)
//
// `cash_movements` é o LIVRO — tudo que entrou e saiu, em qualquer forma. Isto
// aqui é a GAVETA: o dinheiro físico que o dono conta ao abrir a loja, usa para
// troco e confere à noite. Pix e cartão não passam por ela e por isso não
// entram na conta do esperado.
// =============================================================================

interface PresetLancamento {
  titulo: string;
  tipo: CashFlowType;
  categoria: CashCategory;
  forma: PaymentMethod | '';
  /** Com rota, o botão LEVA para outra tela em vez de abrir o formulário. */
  para?: string;
}

const GASTOS_DA_GAVETA: PresetLancamento[] = [
  { titulo: 'Pagar conta', tipo: 'saida', categoria: 'despesa_fixa', forma: 'dinheiro' },
  // 095: comprar peça é "Paguei fornecedor" — a compra vira conta paga e entra
  // na lista do mês. O formulário daqui não oferece mais compra_mercadoria.
  { titulo: 'Comprar peça', tipo: 'saida', categoria: 'compra_mercadoria', forma: 'dinheiro', para: '/compra-na-hora' },
  { titulo: 'Retirar salário', tipo: 'saida', categoria: 'salario', forma: 'dinheiro' },
  { titulo: 'Sangria', tipo: 'saida', categoria: 'sangria', forma: 'dinheiro' },
];

function GavetaDoDia({ aoGastar }: { aoGastar: (p: PresetLancamento) => void }) {
  const cliente = useQueryClient();
  const [fundo, setFundo] = useState('');
  const [contado, setContado] = useState('');
  const [observacao, setObservacao] = useState('');
  const [fechando, setFechando] = useState(false);

  const { data: gaveta, error } = useQuery({
    queryKey: ['caixa', 'gaveta'],
    queryFn: () => caixaDoDia(),
    refetchInterval: 30_000,
  });

  const abrir = useMutation({
    mutationFn: async () => {
      const n = paraNumero(fundo);
      if (n === null || n < 0) throw new Error('Conte o que está na gaveta — pode ser zero.');
      await abrirCaixa(n);
    },
    onSuccess: () => { setFundo(''); cliente.invalidateQueries({ queryKey: ['caixa'] }); },
  });

  const fechar = useMutation({
    mutationFn: async () => {
      const n = paraNumero(contado);
      if (n === null || n < 0) throw new Error('Conte o dinheiro da gaveta antes de fechar.');
      return fecharCaixa({ contado: n, observacao: observacao.trim() || null });
    },
    onSuccess: () => {
      setFechando(false); setContado(''); setObservacao('');
      cliente.invalidateQueries({ queryKey: ['caixa'] });
    },
  });

  if (!gaveta) return null;

  const esperado = gaveta.esperado_agora === null ? null : Number(gaveta.esperado_agora);
  // A prévia da diferença enquanto ele conta: ver o número ANTES de confirmar
  // evita o fechamento com dígito trocado, que fica congelado no histórico.
  const contadoNum = paraNumero(contado);
  const previa = contadoNum !== null && esperado !== null ? contadoNum - esperado : null;

  // --- Fechada: o dia ainda não começou -------------------------------------
  if (!gaveta.aberto) {
    return (
      <section className="cartao space-y-3 p-4">
        <h2 className="rotulo">A gaveta hoje</h2>
        <p className="text-sm text-mute">
          O caixa de hoje ainda não foi aberto. Conte o dinheiro que está na
          gaveta agora — é dele que sai o troco do dia.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="grow">
            <label className="rotulo" htmlFor="fundo">Dinheiro na gaveta (R$)</label>
            <input id="fundo" className="campo text-lg" inputMode="decimal" placeholder="0,00"
                   value={fundo} onChange={(e) => setFundo(e.target.value)} />
          </div>
          <button type="button" className="botao-primario" onClick={() => abrir.mutate()}
                  disabled={abrir.isPending}>
            {abrir.isPending ? 'Abrindo…' : 'Abrir caixa'}
          </button>
        </div>
        {error ? <Aviso tipo="erro">{mensagemDeErro(error)}</Aviso> : null}
        {abrir.error ? <Aviso tipo="erro">{mensagemDeErro(abrir.error)}</Aviso> : null}
      </section>
    );
  }

  // --- Fechado: o retrato congelado do fim do dia ----------------------------
  if (gaveta.fechado) {
    const dif = Number(gaveta.valor_contado ?? 0) - Number(gaveta.valor_esperado_congelado ?? 0);
    return (
      <section className="cartao space-y-2 p-4">
        <div className="flex items-baseline justify-between">
          <h2 className="rotulo">A gaveta hoje</h2>
          <span className="text-[0.625rem] uppercase tracking-wide text-dim">fechada</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <p className="rotulo">Contado</p>
            <p className="font-mono text-base font-black tabular-nums">{brl(Number(gaveta.valor_contado ?? 0))}</p>
          </div>
          <div>
            <p className="rotulo">Esperado</p>
            <p className="font-mono text-base tabular-nums text-mute">{brl(Number(gaveta.valor_esperado_congelado ?? 0))}</p>
          </div>
          <div>
            <p className="rotulo">Diferença</p>
            <p className={[
              'font-mono text-base font-black tabular-nums',
              Math.abs(dif) < 0.005 ? 'text-ok' : 'text-erro',
            ].join(' ')}>
              {dif > 0 ? '+' : ''}{brl(dif)}
            </p>
          </div>
        </div>
        <p className="text-xs text-dim">
          Fechada por {gaveta.fechado_por_nome ?? '—'}
          {Math.abs(dif) < 0.005 ? ' · bateu certinho' : dif > 0 ? ' · sobrou' : ' · faltou'}
        </p>
      </section>
    );
  }

  // --- Aberta: o dia em curso ------------------------------------------------
  return (
    <section className="cartao space-y-3 p-4">
      <div className="flex items-baseline justify-between">
        <h2 className="rotulo">A gaveta hoje</h2>
        <span className="text-[0.625rem] uppercase tracking-wide text-dim">
          aberta por {gaveta.aberto_por_nome ?? '—'}
        </span>
      </div>

      <div>
        <p className="rotulo">Deve ter na gaveta agora</p>
        <p className="text-3xl font-black tabular-nums">{brl(esperado ?? 0)}</p>
        <p className="text-xs text-dim">
          abertura {brl(Number(gaveta.fundo_inicial ?? 0))}
          {' '}+ entradas em dinheiro {brl(Number(gaveta.entradas_dinheiro))}
          {' '}− saídas {brl(Number(gaveta.saidas_dinheiro))}
        </p>
      </div>

      {gaveta.vendas_a_conferir > 0 && (
        <p className="text-xs text-mute">
          {gaveta.vendas_a_conferir} venda{gaveta.vendas_a_conferir > 1 ? 's' : ''} de hoje
          {' '}ainda sem conferir · {gaveta.vendas_conferidas} conferida{gaveta.vendas_conferidas === 1 ? '' : 's'}
        </p>
      )}

      {/* Tirar dinheiro da gaveta é lançamento de saída como outro qualquer —
          estes botões só poupam a digitação de categoria e forma. */}
      <div className="flex flex-wrap gap-1.5">
        {GASTOS_DA_GAVETA.map((g) => (g.para ? (
          <Link key={g.titulo} to={g.para}
                className="border border-edge px-2.5 py-1.5 text-xs font-bold hover:border-signal">
            {g.titulo}
          </Link>
        ) : (
          <button key={g.titulo} type="button" onClick={() => aoGastar(g)}
                  className="border border-edge px-2.5 py-1.5 text-xs font-bold hover:border-signal">
            {g.titulo}
          </button>
        )))}
      </div>

      {fechando ? (
        <div className="space-y-2 border-t border-edge pt-3">
          <div>
            <label className="rotulo" htmlFor="contado">Contei na gaveta (R$)</label>
            <input id="contado" className="campo text-lg" inputMode="decimal" placeholder="0,00"
                   value={contado} onChange={(e) => setContado(e.target.value)} autoFocus />
          </div>
          {previa !== null && (
            <p className={[
              'font-mono text-sm tabular-nums',
              Math.abs(previa) < 0.005 ? 'text-ok' : 'text-erro',
            ].join(' ')}>
              {Math.abs(previa) < 0.005
                ? 'Bate certinho.'
                : previa > 0 ? `Sobra ${brl(previa)}.` : `Falta ${brl(Math.abs(previa))}.`}
            </p>
          )}
          <div>
            <label className="rotulo" htmlFor="obs-fecho">Observação</label>
            <input id="obs-fecho" className="campo" value={observacao}
                   onChange={(e) => setObservacao(e.target.value)}
                   placeholder="Troco dado a mais na venda das 15h…" />
          </div>
          {fechar.error ? <Aviso tipo="erro">{mensagemDeErro(fechar.error)}</Aviso> : null}
          <div className="flex gap-2">
            <button type="button" className="botao-primario grow" onClick={() => fechar.mutate()}
                    disabled={fechar.isPending}>
              {fechar.isPending ? 'Fechando…' : 'Fechar o caixa'}
            </button>
            <button type="button" className="border border-edge px-3 text-sm text-mute"
                    onClick={() => setFechando(false)}>
              Agora não
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setFechando(true)}
                className="w-full border border-edge py-2.5 text-sm font-bold hover:border-signal">
          Fechar o caixa do dia
        </button>
      )}

      {error ? <Aviso tipo="erro">{mensagemDeErro(error)}</Aviso> : null}
    </section>
  );
}

/** As cédulas que o cliente costuma entregar — atalho para o troco sair sem
 *  digitação no meio do atendimento. Só as que cobrem a venda. */
const CEDULAS = [5, 10, 20, 50, 100, 200];

function LinhaDoBalcao({ v }: { v: VendaResumo }) {
  const cliente = useQueryClient();
  const [conferindo, setConferindo] = useState(false);
  const [recebido, setRecebido] = useState('');

  const conferir = useMutation({
    mutationFn: async (valor?: number | null) => {
      const n = valor !== undefined ? valor : paraNumero(recebido);
      if (recebido.trim() && n === null) throw new Error('Valor recebido inválido.');
      await conferirVenda(v.id, n);
    },
    onSuccess: () => {
      setConferindo(false); setRecebido('');
      cliente.invalidateQueries({ queryKey: ['caixa'] });
    },
  });
  const desfazer = useMutation({
    mutationFn: () => desconferirVenda(v.id),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['caixa'] }),
  });

  const total = Number(v.total);
  // 075: na venda DIVIDIDA o troco é sobre a PARTE em dinheiro — o cartão não
  // passa pela gaveta. Em dinheiro puro é o total; em pix/cartão, nada.
  // conferir_venda confere o recebido contra a mesma base.
  const naGaveta = parteEmDinheiro(v.forma_pagamento, v.sale_payments, total);
  const digitado = paraNumero(recebido);
  const troco = digitado === null ? null : digitado - naGaveta;
  const emDinheiro = naGaveta > 0;

  return (
    <li className="py-2 text-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate">
            <span className="font-mono text-xs text-dim">{horaBR(v.vendida_em)}</span>
            {v.vendedor && <span className="ml-2 font-semibold">{v.vendedor}</span>}
            {v.cliente && <span className="ml-2 text-mute">· {v.cliente}</span>}
          </p>
          <p className="text-xs text-dim">
            {v.forma_pagamento === 'fiado'
              ? 'fiado — só entra no caixa no acerto'
              : v.forma_pagamento
                ? descreverPagamento(v.forma_pagamento, v.sale_payments)
                : 'sem forma'}
            {v.conferida_em && (
              <span className="ml-2 text-ok">
                ✓ conferida{v.conferida_por_nome ? ` por ${v.conferida_por_nome}` : ''}
                {v.valor_recebido !== null && v.valor_recebido !== undefined && (
                  <> · recebi {brl(Number(v.valor_recebido))}, troco {brl(Number(v.valor_recebido) - naGaveta)}</>
                )}
              </span>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="font-mono font-bold tabular-nums">{brl(total)}</span>
          {v.conferida_em ? (
            <button type="button" onClick={() => desfazer.mutate()} disabled={desfazer.isPending}
                    className="border border-edge px-2 py-1 text-[0.625rem] uppercase text-dim hover:border-signal"
                    title="Desfazer a conferência">
              desfazer
            </button>
          ) : (
            <button
              type="button"
              onClick={() => (emDinheiro ? setConferindo((c) => !c) : conferir.mutate(null))}
              disabled={conferir.isPending}
              className="border border-ok/60 px-2.5 py-1 text-xs font-bold text-ok hover:bg-ok/10"
              title="Esta venda passou pelo caixa"
            >
              ✓
            </button>
          )}
        </div>
      </div>

      {/* Só em dinheiro: é onde existe troco para conferir. */}
      {conferindo && !v.conferida_em && (
        <div className="mt-2 space-y-2 border-l-2 border-ok/40 pl-3">
          <div className="flex flex-wrap gap-1.5">
            {CEDULAS.filter((c) => c >= naGaveta).slice(0, 4).map((c) => (
              <button key={c} type="button" onClick={() => setRecebido(String(c))}
                      className="border border-edge px-2 py-1 text-xs font-mono hover:border-signal">
                {brl(c)}
              </button>
            ))}
            <button type="button" onClick={() => setRecebido(naGaveta.toFixed(2).replace('.', ','))}
                    className="border border-edge px-2 py-1 text-xs hover:border-signal">
              valor exato
            </button>
          </div>
          <div className="flex items-end gap-2">
            <div className="grow">
              <label className="rotulo" htmlFor={`rec-${v.id}`}>Cliente entregou (R$)</label>
              <input id={`rec-${v.id}`} className="campo" inputMode="decimal" placeholder="0,00"
                     value={recebido} onChange={(e) => setRecebido(e.target.value)} />
            </div>
            <button type="button" className="botao-primario" onClick={() => conferir.mutate(undefined)}
                    disabled={conferir.isPending}>
              Conferir
            </button>
          </div>
          {troco !== null && (
            <p className={[
              'font-mono text-sm tabular-nums',
              troco < 0 ? 'text-erro' : 'text-paper',
            ].join(' ')}>
              {troco < 0
                ? `Falta ${brl(Math.abs(troco))} para fechar a venda.`
                : <>Troco <span className="text-lg font-black">{brl(troco)}</span></>}
            </p>
          )}
          {conferir.error ? <Aviso tipo="erro">{mensagemDeErro(conferir.error)}</Aviso> : null}
        </div>
      )}
      {desfazer.error ? <Aviso tipo="erro">{mensagemDeErro(desfazer.error)}</Aviso> : null}
    </li>
  );
}

function Quebra({
  titulo, itens, corDa,
}: {
  titulo: string;
  itens: Array<[string, { entrada: number; saida: number }]>;
  corDa?: (chave: string) => string | undefined;
}) {
  if (itens.length === 0) return null;
  return (
    <section className="cartao p-4">
      <h2 className="rotulo">{titulo}</h2>
      <ul className="mt-1 divide-y divide-edge">
        {itens.map(([chave, v]) => (
          <li key={chave} className="flex items-center justify-between gap-3 py-2 text-sm">
            <span className="flex items-center gap-2 capitalize">
              {corDa?.(chave) && <span className={`size-2 shrink-0 ${corDa(chave)}`} aria-hidden />}
              {chave.replace(/_/g, ' ')}
            </span>
            <span className="font-mono text-xs">
              {v.entrada > 0 && <span className="text-ok">+{brl(v.entrada)}</span>}
              {v.entrada > 0 && v.saida > 0 && ' · '}
              {v.saida > 0 && <span className="text-erro">−{brl(v.saida)}</span>}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function NovoLancamento({
  aoFechar, aoSalvar, inicial,
}: { aoFechar: () => void; aoSalvar: () => void; inicial?: PresetLancamento | null }) {
  const [tipo, setTipo] = useState<CashFlowType>(inicial?.tipo ?? 'saida');
  const [categoria, setCategoria] = useState<CashCategory>(inicial?.categoria ?? 'despesa_variavel');
  const [forma, setForma] = useState<PaymentMethod | ''>(inicial?.forma ?? 'dinheiro');
  const [valor, setValor] = useState('');
  const [descricao, setDescricao] = useState('');
  const [quando, setQuando] = useState(() => {
    const agora = new Date();
    agora.setMinutes(agora.getMinutes() - agora.getTimezoneOffset());
    return agora.toISOString().slice(0, 16);
  });

  const salvar = useMutation({
    mutationFn: async () => {
      const n = paraNumero(valor);
      if (n === null || n <= 0) throw new Error('Informe um valor maior que zero.');
      await lancarCaixa({
        tipo,
        categoria,
        forma: forma || null,
        valor: n,
        descricao: descricao.trim() || null,
        ocorridoEm: new Date(quando).toISOString(),
      });
    },
    onSuccess: aoSalvar,
  });

  const categoriasDoTipo = categoriasDoLancamento(tipo);

  return (
    <section className="cartao space-y-4 border-signal/50 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold">{inicial?.titulo ?? 'Novo lançamento'}</h2>
        <button type="button" onClick={aoFechar} className="px-2 text-sm text-dim">✕</button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {(['entrada', 'saida'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              setTipo(t);
              setCategoria(t === 'entrada' ? 'aporte' : 'despesa_variavel');
            }}
            aria-pressed={tipo === t}
            className={[
              'border px-4 py-3 font-bold',
              tipo === t
                ? t === 'entrada' ? 'border-ok bg-ok/15 text-ok' : 'border-erro bg-erro/15 text-erro'
                : 'border-edge text-mute',
            ].join(' ')}
          >
            {t === 'entrada' ? 'Entrada' : 'Saída'}
          </button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor="categoria">Categoria</label>
          <select id="categoria" className="campo" value={categoria}
                  onChange={(e) => setCategoria(e.target.value as CashCategory)}>
            {categoriasDoTipo.map((c) => <option key={c.id} value={c.id}>{c.rotulo}</option>)}
          </select>
          {tipo === 'saida' && (
            <p className="mt-1 text-xs text-dim">
              Compra de fornecedor?{' '}
              <Link to="/compra-na-hora" className="underline">Paguei fornecedor →</Link>
            </p>
          )}
        </div>
        <div>
          <label className="rotulo" htmlFor="forma">Forma</label>
          <select id="forma" className="campo" value={forma}
                  onChange={(e) => setForma(e.target.value as PaymentMethod | '')}>
            <option value="">Não informar</option>
            {FORMAS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className="rotulo" htmlFor="valor">Valor (R$)</label>
          <input id="valor" className="campo text-lg" inputMode="decimal" placeholder="0,00"
                 value={valor} onChange={(e) => setValor(e.target.value)} autoFocus />
        </div>
        <div>
          <label className="rotulo" htmlFor="quando">Data e hora</label>
          <input id="quando" type="datetime-local" className="campo" value={quando}
                 onChange={(e) => setQuando(e.target.value)} />
        </div>
      </div>

      <div>
        <label className="rotulo" htmlFor="descricao">Descrição</label>
        <input id="descricao" className="campo" value={descricao}
               onChange={(e) => setDescricao(e.target.value)}
               placeholder="Almoço da equipe, conta de luz, troco inicial…" />
      </div>

      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}

      <button type="button" className="botao-primario w-full" onClick={() => salvar.mutate()}
              disabled={salvar.isPending}>
        {salvar.isPending ? 'Lançando…' : 'Lançar'}
      </button>
    </section>
  );
}
